"use client";

import {
  FC,
  useRef,
  useEffect,
  useMemo,
  createRef,
  RefObject,
  useState,
} from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import { type Generation as TGeneration } from "@/types/thread";
import { useAppSelector } from "@/redux/store";
import DailyGensSummary from "../MobileLayout/DailyGensSummary";
import DailyGensSummaryCompact from "../MobileLayout/DailyGensSummaryCompact";
import CreatorSummary from "./CreatorSummary";
import Generation from "../Generation";
import GenerationStatus from "../GenerationStatus";
import ScrollArrow from "@/components/common/ScrollArrow";
import InfiniteScroll from "@/components/common/InfiniteScroll";

const createGenerationRefs = (
  generations: TGeneration[] | undefined
): { [key: number]: RefObject<HTMLDivElement> } => {
  const refs: { [key: number]: RefObject<HTMLDivElement> } = {};

  if (Array.isArray(generations)) {
    generations.forEach((generation) => {
      if (!refs[generation.id]) {
        refs[generation.id] = createRef<HTMLDivElement>();
      }
    });
  }

  return refs;
};

// Helper function to find the scrolling parent
const findScrollParent = (
  element: HTMLElement | null
): HTMLElement | Window => {
  if (!element) return window;

  let parent = element.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    if (
      style.overflowY === "auto" ||
      style.overflowY === "scroll" ||
      style.overflow === "auto" ||
      style.overflow === "scroll"
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
};

interface ThreadProps {
  publicView: boolean;
  title?: Nullable<string>;
  onToggleComments: () => void;
}

const Thread: FC<ThreadProps> = ({ publicView, title, onToggleComments }) => {
  const { classes } = useStyles();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("md")
  );
  const pathname = usePathname() as string;
  const contentRef = useRef<HTMLDivElement>(null);
  const dailyGensSummaryRef = useRef<HTMLDivElement>(null);
  const [showCompactSummary, setShowCompactSummary] = useState(false);
  const scrollParentRef = useRef<HTMLElement | Window | null>(null);

  const scrolledGenerationsRef = useRef<Set<number>>(new Set());
  const userHasScrolledRef = useRef(false);
  const lastGenerationCountRef = useRef(0);
  const wasGeneratingRef = useRef(false);
  const lastScrolledIdRef = useRef<string | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMountRef = useRef(true);
  const scrollDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const selectedGeneration = useAppSelector(
    (state) => state.room.selectedGeneration
  );
  const generations = useAppSelector((state) => state.room.generations);
  const selectedGenerationIndex =
    selectedGeneration &&
    generations.findIndex((gen) => gen.id === selectedGeneration);
  const jobStatus = useAppSelector((state) => state.room.jobStatus);
  const threadProperties = useAppSelector((state) => state.room.roomProperties);
  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user?.subscriptionType !== "free");
  const displayedGenerations = useMemo(
    () =>
      Array.isArray(generations)
        ? [...generations].sort((a, b) =>
            a.createdAt.localeCompare(b.createdAt)
          )
        : [],
    [generations]
  );

  const generationRefs = useMemo(
    () => createGenerationRefs(generations),
    [generations]
  );
  const jobId =
    user?.activeJobs && user.activeJobs.length > 0
      ? user.activeJobs[0].id
      : null;
  const hasActiveJobs = Boolean(jobId);

  // Find scroll parent on mount
  useEffect(() => {
    if (contentRef.current) {
      scrollParentRef.current = findScrollParent(contentRef.current);
    }
  }, []);

  useEffect(() => {
    if (
      !isMobile ||
      isSubscribed ||
      generations.length === 0 ||
      !dailyGensSummaryRef.current ||
      !scrollParentRef.current
    )
      return;

    const handleScroll = () => {
      if (dailyGensSummaryRef.current) {
        const summaryRect = dailyGensSummaryRef.current.getBoundingClientRect();
        // Check if scrolled past the navbar (50px on mobile)
        setShowCompactSummary(summaryRect.bottom < 50);
      }
    };

    const scrollElement = scrollParentRef.current;

    if (scrollElement === window) {
      window.addEventListener("scroll", handleScroll);
    } else {
      (scrollElement as HTMLElement).addEventListener("scroll", handleScroll);
    }

    handleScroll();

    return () => {
      if (scrollElement === window) {
        window.removeEventListener("scroll", handleScroll);
      } else {
        (scrollElement as HTMLElement).removeEventListener(
          "scroll",
          handleScroll
        );
      }
    };
  }, [isMobile, isSubscribed, generations.length]);

  useEffect(() => {
    const generationIdFromUrl = searchParams?.get("id");

    // Only auto-scroll on initial mount or when navigating from sidebar
    if (
      generationIdFromUrl &&
      isInitialMountRef.current &&
      generations.length > 0 &&
      !userHasScrolledRef.current
    ) {
      const targetGenerationId = parseInt(generationIdFromUrl);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      const attemptScroll = (retriesLeft = 10) => {
        const targetRef = generationRefs[targetGenerationId];

        if (targetRef?.current) {
          lastScrolledIdRef.current = generationIdFromUrl;

          targetRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // Mark initial mount as done after successful scroll
          isInitialMountRef.current = false;
        } else if (retriesLeft > 0) {
          console.log(`Generation ref not found, retries left: ${retriesLeft}`);

          scrollTimeoutRef.current = setTimeout(() => {
            attemptScroll(retriesLeft - 1);
          }, 300);
        } else {
          console.error("Failed to find generation element after all retries");
          isInitialMountRef.current = false;
        }
      };

      scrollTimeoutRef.current = setTimeout(() => {
        attemptScroll();
      }, 500);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [searchParams, generations.length, generationRefs]);

  useEffect(() => {
    if (!scrollParentRef.current) return;

    const handleScroll = () => {
      userHasScrolledRef.current = true;

      // Clear any pending URL updates when user scrolls
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }
    };

    const scrollElement = scrollParentRef.current;

    if (scrollElement === window) {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    } else {
      (scrollElement as HTMLElement).addEventListener("scroll", handleScroll);
      return () =>
        (scrollElement as HTMLElement).removeEventListener(
          "scroll",
          handleScroll
        );
    }
  }, []);

  useEffect(() => {
    if (!publicView && generations.length > 0) {
      const latestGeneration = generations[generations.length - 1];

      const isComplete =
        latestGeneration.images && latestGeneration.images.length > 0;
      const hasNotScrolledToThis = !scrolledGenerationsRef.current.has(
        latestGeneration.id
      );
      const justCompleted = wasGeneratingRef.current && !hasActiveJobs;

      if (hasActiveJobs) {
        wasGeneratingRef.current = true;
      }

      if (justCompleted && isComplete && hasNotScrolledToThis) {
        scrolledGenerationsRef.current.add(latestGeneration.id);

        userHasScrolledRef.current = false;
        wasGeneratingRef.current = false;

        setTimeout(() => {
          let attempts = 0;
          const tryScroll = () => {
            const generationRef = generationRefs[latestGeneration.id];

            if (generationRef?.current && !userHasScrolledRef.current) {
              generationRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            } else if (attempts < 5 && !generationRef?.current) {
              attempts++;
              setTimeout(tryScroll, 200);
            }
          };

          tryScroll();
        }, 1000);
      }

      lastGenerationCountRef.current = generations.length;
    }
  }, [generations, generationRefs, publicView, hasActiveJobs]);

  // useEffect(() => {
  //   let currentId: number | null = null;

  //   const handleIntersection = (entries: any[]) => {
  //     entries.forEach((entry: any) => {
  //       if (entry.isIntersecting) {
  //         const newId = Number(entry.target.id);
  //         if (typeof newId !== "number" || newId === currentId) return;

  //         currentId = newId;

  //         // Only update URL after user has stopped scrolling for 1 second
  //         if (scrollDebounceRef.current) {
  //           clearTimeout(scrollDebounceRef.current);
  //         }

  //         scrollDebounceRef.current = setTimeout(() => {
  //           router.replace(`${pathname}?id=${newId}`, { scroll: false });
  //         }, 1000);
  //       }
  //     });
  //   };

  //   const observer = new IntersectionObserver(handleIntersection, {
  //     root: null,
  //     threshold: 0.5,
  //   });

  //   Object.values(generationRefs).forEach((ref) => {
  //     if (ref.current) {
  //       observer.observe(ref.current);
  //     }
  //   });

  //   return () => {
  //     if (scrollDebounceRef.current) {
  //       clearTimeout(scrollDebounceRef.current);
  //     }
  //     Object.values(generationRefs).forEach((ref) => {
  //       if (ref.current) {
  //         observer.unobserve(ref.current);
  //       }
  //     });
  //   };
  // }, [pathname, generationRefs]);

  return (
    <div className={classes.content} ref={contentRef} id="thread-content">
      {isMobile &&
        showCompactSummary &&
        !isSubscribed &&
        generations.length > 0 && (
          <div className={classes.stickyCompactSummary}>
            <DailyGensSummaryCompact />
          </div>
        )}
      {isMobile && generations.length !== 0 && (
        <div ref={dailyGensSummaryRef}>
          <DailyGensSummary />
        </div>
      )}
      {threadProperties?.username && !isMobile && (
        <CreatorSummary
          username={threadProperties.username}
          avatarUrl={threadProperties.avatarUrl}
          onToggleComments={onToggleComments}
          createdAt={threadProperties.createdAt}
        />
      )}
      <div className={classes.generationsContent}>
        <InfiniteScroll
          list={displayedGenerations}
          listItem={Generation}
          showAds
          adFrequency={1}
          entryIndex={
            selectedGenerationIndex && selectedGenerationIndex > 0
              ? selectedGenerationIndex
              : undefined
          }
          componentProps={{
            isSubscribed,
            editEnabled: !publicView,
            generationRefs,
            showFeedControls: false,
          }}
          className={classes.generations}
        />
        {!publicView && jobId && <GenerationStatus status={jobStatus} />}
      </div>
      <ScrollArrow parentId="thread-content" color="secondary" bottom={40} />
    </div>
  );
};

export default Thread;
